'use client';
import React, { useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import { getCurrencyFromLocale, formatCurrency } from '@/i18n/utils';
import { clearSettingsCache, getCurrencyRates } from '@/i18n/site-settings-integration';
import { useCheckoutStore } from '@/stores/cartStore';

/**
 * Props for DynamicPrice component.
 * @param basePrice - The price including VAT (gross price).
 * @param vat_number - The user's VAT number (if any). If set, price will be shown excluding VAT.
 * @param vat_rate - The VAT rate as a decimal (e.g., 0.25 for 25%).
 * @param fixedCurrency - Optional fixed currency code for display (for orders).
 * @param fixedAmount - Optional fixed amount for display (for orders).
 * @param forceRefresh - Optional flag to force refreshing currency rates from database.
 * @param isB2B - Whether the transaction is B2B.
 */
interface DynamicPriceProps {
  basePrice: number;
  vat_number?: string;
  vat_rate?: number;
  fixedCurrency?: string;
  fixedAmount?: number;
  forceRefresh?: boolean;
  isB2B?: boolean;
}

export default function DynamicPrice({
  basePrice,
  vat_number: propVatNumber = '',
  vat_rate = 0.25,
  fixedCurrency,
  fixedAmount,
  forceRefresh = false,
  isB2B = false,
}: DynamicPriceProps) {
  const routeLocale = useLocale();
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const targetCurrency = getCurrencyFromLocale(routeLocale);

  // Always call the hook at the top level
  const checkoutStore = useCheckoutStore();
  const storeVatNumber = checkoutStore?.vatNumber || '';
  const storeVatValid = checkoutStore?.vatValid || false;
  // Use propVatNumber if provided, otherwise use storeVatNumber if valid
  const vatNumber = propVatNumber || (storeVatValid ? storeVatNumber : '');

  // Compute net price if VAT number is set (i.e., user is VAT exempt)
  const netBasePrice = vatNumber.trim() ? (isB2B ? basePrice : basePrice / (1 + vat_rate)) 
  : basePrice;

  useEffect(() => {
    const refreshRates = async () => {
      try {
        if (forceRefresh) await clearSettingsCache();
        const rates = getCurrencyRates(forceRefresh);
        const rate = rates[targetCurrency] || 1;
        const converted = netBasePrice * rate;
        setConvertedAmount(converted);
      } catch (error) {
        console.error('Error refreshing currency rates:', error);
        const rates = getCurrencyRates(false);
        const rate = rates[targetCurrency] || 1;
        setConvertedAmount(netBasePrice * rate);
      }
    };
    refreshRates();
  }, [netBasePrice, targetCurrency, forceRefresh]);

  // If fixed currency info is provided (from order data), use that
  if (fixedCurrency !== undefined && fixedAmount !== undefined) {
    return (
      <span>
        {formatCurrency(fixedAmount, routeLocale, fixedCurrency)}
      </span>
    );
  }

  // Show loading state while calculating
  if (convertedAmount === null) {
    return <span>...</span>;
  }

  // Return the converted and formatted amount
  return (
    <span>
      {formatCurrency(convertedAmount, routeLocale, targetCurrency)}
    </span>
  );
} 